import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import Shell from '../components/Shell';
import { TEXTAREA_MAX, TEXT_MAX, initialValue, sentence, validateForm } from '../formFields';
import type { RawValue } from '../formFields';
import type { Branding, ClientForm, Departamento, FieldValue, FormField, LocationValue } from '../types';

interface FormPageProps {
  branding: Branding;
  form: ClientForm;
  busy: boolean;
  error: string | null;
  // 422 from POST /inputs: { key: mensaje }.
  serverErrors: Record<string, string> | null;
  onSubmit: (values: Record<string, FieldValue>) => void;
}

const fieldId = (key: string) => `field-${key}`;
const errorId = (key: string) => `field-${key}-error`;

// step === 'form' (PLAN-Verification-Journeys.md §5.2): the tenant's questions
// for the person, after the capture. Shown to everyone who finished the
// capture, whatever the verdict, so it reveals nothing about it.
export default function FormPage({ branding, form, busy, error, serverErrors, onSubmit }: FormPageProps) {
  const [raw, setRaw] = useState<Record<string, RawValue>>(() =>
    Object.fromEntries(form.fields.map((f) => [f.key, initialValue(f)])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  const focusFirstError = (errs: Record<string, string>) => {
    const first = form.fields.find((f) => errs[f.key]);
    if (!first) return;
    // Next frame: the aria-invalid/error text must be rendered first.
    requestAnimationFrame(() => {
      const el = formRef.current?.querySelector<HTMLElement>(`[data-field="${first.key}"]`);
      el?.focus();
    });
  };

  useEffect(() => {
    if (!serverErrors) return;
    setErrors(serverErrors);
    focusFirstError(serverErrors);
  }, [serverErrors]); // only when a new 422 arrives

  const setValue = (key: string, value: RawValue) => {
    setRaw((r) => ({ ...r, [key]: value }));
    setErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    const { values, errors: errs } = validateForm(form.fields, raw, form.locations);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    onSubmit(values);
  };

  // Server errors for keys that aren't a field of this form ("_", unexpected keys).
  const fieldKeys = useMemo(() => new Set(form.fields.map((f) => f.key)), [form.fields]);
  const otherErrors = Object.entries(errors).filter(([k]) => !fieldKeys.has(k));
  const hasRequired = form.fields.some((f) => f.required);

  return (
    <Shell branding={branding}>
      <h1>Unos datos más</h1>
      <p className="lead">Para terminar, responde las siguientes preguntas.</p>
      {hasRequired && (
        <p className="form-note">
          Los campos marcados con <span className="required-mark">*</span> son obligatorios.
        </p>
      )}

      <form ref={formRef} className="client-form" onSubmit={handleSubmit} noValidate>
        {form.fields.map((field) => (
          <FieldControl
            key={field.key}
            field={field}
            value={raw[field.key] ?? initialValue(field)}
            error={errors[field.key]}
            locations={form.locations}
            disabled={busy}
            onChange={(v) => setValue(field.key, v)}
          />
        ))}

        {otherErrors.map(([k, msg]) => (
          <p key={k} className="error-text" role="alert">
            {sentence(msg)}
          </p>
        ))}
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Enviando…' : 'Enviar'}
        </button>
      </form>
    </Shell>
  );
}

interface FieldControlProps {
  field: FormField;
  value: RawValue;
  error: string | undefined;
  locations: Departamento[] | null;
  disabled: boolean;
  onChange: (value: RawValue) => void;
}

function FieldControl({ field, value, error, locations, disabled, onChange }: FieldControlProps) {
  const id = fieldId(field.key);
  const invalid = !!error;
  const common = {
    id,
    'data-field': field.key,
    disabled,
    'aria-invalid': invalid || undefined,
    'aria-required': field.required || undefined,
    'aria-describedby': invalid ? errorId(field.key) : undefined,
  };

  const label = (
    <>
      {field.label}
      {field.required && (
        <span className="required-mark" aria-hidden="true">
          {' '}*
        </span>
      )}
    </>
  );

  const errorText = invalid && (
    <p id={errorId(field.key)} className="field-error">
      {sentence(error)}
    </p>
  );

  // Radio and location groups label a fieldset; everything else a single control.
  if (field.type === 'boolean') {
    return (
      <fieldset className={`form-field${invalid ? ' form-field--invalid' : ''}`}>
        <legend className="field-label">{label}</legend>
        <div className="choice-group" role="radiogroup" aria-describedby={common['aria-describedby']}>
          {[
            { v: true, text: 'Sí' },
            { v: false, text: 'No' },
          ].map((opt, i) => (
            <label key={opt.text} className="choice">
              <input
                type="radio"
                name={id}
                data-field={i === 0 ? field.key : undefined}
                checked={value === opt.v}
                disabled={disabled}
                onChange={() => onChange(opt.v)}
              />
              <span>{opt.text}</span>
            </label>
          ))}
        </div>
        {errorText}
      </fieldset>
    );
  }

  if (field.type === 'location') {
    return (
      <LocationControl
        field={field}
        value={value as LocationValue}
        locations={locations ?? []}
        label={label}
        error={errorText}
        invalid={invalid}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  let control;
  const str = typeof value === 'string' ? value : '';
  switch (field.type) {
    case 'textarea':
      control = (
        <>
          <textarea
            {...common}
            className="field-input"
            rows={4}
            maxLength={TEXTAREA_MAX}
            value={str}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="field-hint" aria-hidden="true">
            {str.length}/{TEXTAREA_MAX}
          </span>
        </>
      );
      break;
    case 'number':
      control = (
        <input
          {...common}
          className="field-input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case 'money':
      control = (
        <div className="input-affix">
          <span className="affix" aria-hidden="true">
            Q
          </span>
          <input
            {...common}
            className="field-input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.00"
            value={str}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="sr-only">quetzales (GTQ)</span>
        </div>
      );
      break;
    case 'percent':
      control = (
        <div className="input-affix">
          <input
            {...common}
            className="field-input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={str}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="affix" aria-hidden="true">
            %
          </span>
          <span className="sr-only">por ciento, de 0 a 100</span>
        </div>
      );
      break;
    case 'select':
      control = (
        <select {...common} className="field-input" value={str} onChange={(e) => onChange(e.target.value)}>
          <option value="">Selecciona una opción</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case 'date':
      control = (
        <input
          {...common}
          className="field-input"
          type="date"
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    default: // text
      control = (
        <input
          {...common}
          className="field-input"
          type="text"
          maxLength={TEXT_MAX}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  return (
    <div className={`form-field${invalid ? ' form-field--invalid' : ''}`}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {control}
      {errorText}
    </div>
  );
}

interface LocationControlProps {
  field: FormField;
  value: LocationValue;
  locations: Departamento[];
  label: ReactNode;
  error: ReactNode;
  invalid: boolean;
  disabled: boolean;
  onChange: (value: RawValue) => void;
}

// Departamento → municipio: a municipio's INE code starts with its departamento's.
function LocationControl({ field, value, locations, label, error, invalid, disabled, onChange }: LocationControlProps) {
  const depId = `${fieldId(field.key)}-dep`;
  const munId = `${fieldId(field.key)}-mun`;
  const describedBy = invalid ? errorId(field.key) : undefined;
  const dep = locations.find((d) => d.code === value.departamentoCode);
  const municipios = dep ? dep.municipios.filter((m) => m.code.startsWith(dep.code)) : [];

  return (
    <fieldset className={`form-field${invalid ? ' form-field--invalid' : ''}`}>
      <legend className="field-label">{label}</legend>
      <div className="location-group">
        <label htmlFor={depId} className="field-sublabel">
          Departamento
        </label>
        <select
          id={depId}
          data-field={field.key}
          className="field-input"
          value={value.departamentoCode}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-required={field.required || undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange({ departamentoCode: e.target.value, municipioCode: '' })}
        >
          <option value="">Selecciona un departamento</option>
          {locations.map((d) => (
            <option key={d.code} value={d.code}>
              {d.name}
            </option>
          ))}
        </select>

        <label htmlFor={munId} className="field-sublabel">
          Municipio
        </label>
        <select
          id={munId}
          className="field-input"
          value={value.municipioCode}
          disabled={disabled || !dep}
          aria-invalid={invalid || undefined}
          aria-required={field.required || undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange({ departamentoCode: value.departamentoCode, municipioCode: e.target.value })}
        >
          <option value="">{dep ? 'Selecciona un municipio' : 'Primero elige un departamento'}</option>
          {municipios.map((m) => (
            <option key={m.code} value={m.code}>
              {m.name}
            </option>
          ))}
        </select>
      </div>
      {error}
    </fieldset>
  );
}
